import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "ecl_cookie_consent_v1";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        // Slight delay so it doesn't compete with the hero animation
        const t = setTimeout(() => setVisible(true), 900);
        return () => clearTimeout(t);
      }
    } catch (_) {
      setVisible(true);
    }
  }, []);

  const persist = (value) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ value, ts: new Date().toISOString() })
      );
    } catch (_) {
      /* ignore */
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-[420px] z-[60]"
          data-testid="cookie-banner"
          role="dialog"
          aria-label="Consentement aux cookies"
        >
          <div className="relative bg-white border border-navy-100 shadow-[0_24px_60px_-15px_rgba(0,35,98,0.25)] p-5 md:p-6">
            {/* Accent stripe */}
            <div className="absolute top-0 left-0 w-1 h-full bg-navy-500" />

            <button
              type="button"
              onClick={() => persist("dismissed")}
              aria-label="Fermer"
              data-testid="cookie-banner-close"
              className="absolute top-3 right-3 text-navy-400 hover:text-navy-700 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-start gap-3 mb-4 pr-6">
              <div className="shrink-0 w-9 h-9 bg-navy-50 flex items-center justify-center">
                <Cookie className="h-4 w-4 text-navy-500" strokeWidth={1.6} />
              </div>
              <div>
                <div className="overline mb-1.5">Confidentialité</div>
                <h3 className="font-display text-lg text-navy-700 leading-tight mb-2">
                  Nous respectons votre vie privée
                </h3>
                <p className="text-[13px] leading-relaxed text-[#555]">
                  Nous utilisons des cookies pour améliorer votre expérience, mesurer l'audience du site
                  et vous proposer des contenus adaptés. Vous pouvez accepter, refuser ou en savoir plus.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => persist("accepted")}
                data-testid="cookie-banner-accept"
                className="btn-red text-[11px] py-2.5 px-5"
              >
                Tout accepter
              </button>
              <button
                type="button"
                onClick={() => persist("refused")}
                data-testid="cookie-banner-refuse"
                className="text-[11px] uppercase tracking-[0.2em] font-semibold text-navy-700 hover:text-red-500 transition-colors py-2.5 px-4 border border-navy-200"
              >
                Refuser
              </button>
              <Link
                to="/politique-de-confidentialite"
                onClick={() => persist("dismissed")}
                data-testid="cookie-banner-learn-more"
                className="text-[11px] uppercase tracking-[0.2em] font-medium text-navy-500 hover:text-navy-700 underline underline-offset-4 decoration-navy-200 ml-auto"
              >
                En savoir plus
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
